
/**
 * @typedef {Object} Sermon
 * @property {string} ignore_sku2
 * @property {string} seriesMap
 * @property {string} ignore_sku1
 * @property {string} mergedId
 * @property {string} id
 * @property {string} title
 * @property {string[]} speaker
 * @property {string} date
 * @property {string} postType
 * @property {string} permalink
 * @property {string} imageUrl
 * @property {string[]} scripture
 * @property {string} location
 * @property {string} duration
 * @property {string} video
 * @property {string} audio
 * @property {string} year
 * @property {string} book
 */

/**
 * @typedef {Object} Series
 * @property {string} ignore_sku2
 * @property {string} seriesMap
 * @property {string} ignore_sku1
 * @property {string} mergedId
 * @property {string} id
 * @property {string} title
 * @property {string[]} speaker
 * @property {string} date
 * @property {string} postType
 * @property {string} permalink
 * @property {string} imageUrl
 * @property {Sermon[]} sermons
 */

// series columns: ignore_sku2,seriesMap,ignore_sku1,mergedId,id,title,speaker,date,postType,permalink,imageUrl

import _redeemerSeries from './series.json';
import _redeemerSermons from './sermons.json';

/**
 * @var {Series[]}
 */
const redeemerSeries = _redeemerSeries

/**
 * @var {Sermon[]}
 */
const redeemerSermons = _redeemerSermons

Array.prototype.unique = function() {
	const output = {}
	for(const item of this) {
		output[item] = true
	}
	return Object.keys(output)
}

const cache = new Map()

/**
 * Get formatted sermon data
 * @returns {Object<string, Sermon>}
 */
export const getSermons = () => {
	if(cache.has('sermons')) {
		return cache.get('sermons')
	}

	/**
	 * @type {Object<string, Sermon>}
	 */
	const sermons = {}
	
	for(const singleSermon of redeemerSermons) {
		const formatted = { ...singleSermon }
		
		formatted.speaker   = formatted.speaker.split('|').filter(a => a)
		formatted.scripture = formatted.scripture.split('|').filter(a => a)
		formatted.seriesMap = formatted.seriesMap.toString().split('|').filter(a => a)
		formatted.year      = formatted.date.slice(0, 4)
		formatted.book      = formatted.scripture.map(s => s.match(/\d?\s?[a-zA-Z]+/))

		sermons[singleSermon.id] = formatted
	}

	cache.set('sermons', sermons)

	return sermons
}

/**
 * Get formatted series data
 *
 * @returns {Object<string, Series>}
 */
export const getSeries = () => {
	if(cache.has('series')) {
		return cache.get('series')
	}

	/**
	 * @type {Object<string, Series>}
	 */
	const seriesMap = {}
	
	for(const singleSeries of redeemerSeries) {
		if(!singleSeries.date) continue;
		seriesMap[singleSeries.seriesMap.toString()] = {
			...singleSeries,
			speaker: singleSeries.speaker.split('|').filter(a => a),
			year: singleSeries.date.slice(0, 4),
			book: [],
			sermons: []
		}
	}

	const sermons = getSermons()

	for(const sermon of Object.values(sermons)) {
		for(const seriesId of sermon.seriesMap) {
			if(seriesId in seriesMap) {
				seriesMap[seriesId].sermons.push(sermon)
				seriesMap[seriesId].book = seriesMap[seriesId].book.concat(sermon.book).unique()
			} else {
				// console.error(`Series ${seriesId} not found for sermon ${sermon.id}`)
			}
		}
	}

	// remove series without sermons
	for(const id in seriesMap) {
		if(!seriesMap[id].sermons.length) {
			delete seriesMap[id]
		}
	}

	const series = Object.values(seriesMap).sort((a, b) => a.date > b.date ? 1 : -1)

	cache.set('series', series)

	return series
}

/**
 * Get a list of all referenced speakers
 * @returns {string[]}
 */
export const getSpeakers = () => {
	if(cache.has('speakers')) {
		return cache.get('speakers')
	}

	const sermons = getSermons()
	let output = {}
	for(const id in sermons) {
		sermons[id].speaker.forEach(s => {
			output[s] = true
		})
	}

	const speakers = Object.keys(output).sort().filter(a => a)

	cache.set('speakers', speakers)

	return speakers
}

/**
 * Get all possible years for sermons
 * @returns {string[]}
 */
export const getYears = () => {
	if(!cache.has('years')) {
		cache.set( 'years', Object.values(getSermons())
			.map(s => s.date.slice(0, 4))
			.unique()
			.sort((a, b) => b - a)
			.filter(a => a)
		)
	}

	return cache.get('years')
}

/**
 * Get all possible books for sermons
 * @returns {string[]}
 */
export const getBooks = () => {
	if(!cache.has('books')) {
		cache.set('books', Object.values(getSermons())
			.map(s => s.book)
			.flat()
			.unique()
			.sort()
			.filter(a => a)
		)
	}

	return cache.get('books')
}

/**
 * Filters a series by search keyword based on title and sermon titles
 *
 * @param {Series[]} series - The series to filter
 * @param {string} search - The search keyword
 * @returns {Series[]} - The filtered series
 */
export const filterSeriesBySearch = (series, search) => {
	const lc = search.toLowerCase()
	return series.filter(s => (
		s.title.toLowerCase().includes(lc) ||
		s.sermons.some(sermon => sermon.title.toLowerCase().includes(lc))
	))
}

/**
 * Filter series by filter object
 *
 * @param {Object} filter - The filter object
 * @returns {Series[]} - The filtered series
 */
export const filterSeries = (filter) => {
	const series = getSeries()
	const search = filter.search || false

	delete filter.search

	const filters = Object.entries(filter).map(([key, value]) => {
		if(Array.isArray(value)) {
			return value.length ? [key, value.reduce((acc, val) => ({ ...acc, [val]: true }), {})] : false
		}
		return [key, { [value]: true }]
	}).filter(Boolean)

	const output = []

	for(const singleSeries of series) {
		if(filters.every(([key, filter]) => {
			if(Array.isArray(singleSeries[key])) {
				return singleSeries[key].some(meta => filter[meta])
			} else {
				return !!filter[singleSeries[key]]
			}
		})) {
			output.push(singleSeries)
		}
	}

	if(search) {
		return filterSeriesBySearch(output, search)
	}

	return output
}
